/* eslint-disable no-param-reassign */
import React, { useContext, useState, useEffect } from 'react';
import { shape, bool, string, object } from 'prop-types';
import { useStore, ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useDataModel, params, client } from '@thd-nucleus/data-sources';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType,
} from '@thd-nucleus/data-sources/react/dataModel/prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import { FulfillmentPodPlaceholder } from './FulfillmentPodPlaceholder';
import { getStoreTemplate } from '../helpers/store-helper';
import {
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  isPodStoreEqual
} from '../helpers/pod-fulfillment-utils';
import { EmptyStoreMessage } from '../partials/store/EmptyStoreMessage';
import './fulfillment-pod-store.scss';
import { isShowBoth, isTwoTile, isTwoTileOff } from '../../components/helper/utils';
import { renderPickupTemplates } from '../templates/pickUpTemplate';
import { CHECK_NEARBY_STORE } from '../../components/constants';

export const FulfillmentPodStore = React.memo(({
  itemId, storeId, zipCode, isTwoTileEnabledFS, useCustomTemplate, hideForChooseYourOptions, directData
}) => {
  const { storeId: useStoreId } = useStore();
  const { customer } = useContext(ExperienceContext);
  const [product, setProduct] = useState(null);
  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const isTwoTileMessagingEnabled = useConfigService('fs-prop:product-pod__fulfillment--isTwoTileMessagingEnabled');
  if (!isTwoTileEnabledFS || ((isTwoTile() || isShowBoth()) && !isTwoTileOff())) {
    isTwoTileEnabledFS = (isTwoTileEnabledGlobal && isTwoTileMessagingEnabled)
      || ((isTwoTile() || isShowBoth()) && !isTwoTileOff());
  }
  const { additionalData = {} } = useImpression({
    data: {
      id: itemId || '',
      component: 'ProductStore',
      name: 'ProductStore',
      position: 0
    }
  });
  const opts = {
    variables: {
      itemId,
      storeId: storeId || useStoreId,
      zipCode
    },
    skip: !itemId || !!directData,
    ssr: false
  };
  const { data, loading, error } = useDataModel('product', opts);

  const { product: productData } = data || { product: directData } || {};

  useEffect(() => {
    if (productData) {
      setProduct(productData);
    }
  }, [productData]);

  if (loading || (
    typeof (data) === 'undefined' && !error && !product
  )) return <FulfillmentPodPlaceholder />;
  if (!product) { return EmptyStoreMessage; }

  const isHDQC = product?.info?.productSubType?.name === 'HDQC';
  const { scheduleAMeasure = false } = product?.installServices || {};
  const isScheduleAConsultationEligible = isCustomKitchenCabinetProduct(product);
  const isRequestAQuoteEligible = isBrioProduct(product);

  if ((hideForChooseYourOptions && product?.info?.isGenericProduct)
    || scheduleAMeasure
    || isScheduleAConsultationEligible
    || isRequestAQuoteEligible
    || isHDQC) {
    return null;
  }

  const { parent } = additionalData || {};

  const handleClick = () => {
    LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', {
      podAction: CHECK_NEARBY_STORE,
      podAnchorSku: itemId,
      parent,
      target: ''
    });
  };

  const StoreComponent = getStoreTemplate(
    {
      product,
      storeId: storeId || useStoreId,
      customer,
      isTwoTileEnabledFS,
      customRender: useCustomTemplate ? renderPickupTemplates : null
    });

  if (!StoreComponent) { return EmptyStoreMessage; }

  return (
    <span data-component="FulfillmentPodStore">
      <StoreComponent
        product={product}
        onCheckNearbyStoresClick={handleClick}
        isTwoTileEnabledFS={isTwoTileEnabledFS}
      />
    </span>
  );
}, isPodStoreEqual);

FulfillmentPodStore.propTypes = {
  itemId: string.isRequired,
  storeId: string,
  zipCode: string,
  useCustomTemplate: bool,
  isTwoTileEnabledFS: bool,
  hideForChooseYourOptions: bool,
  // eslint-disable-next-line react/forbid-prop-types
  directData: object,
};

FulfillmentPodStore.defaultProps = {
  useCustomTemplate: false,
  isTwoTileEnabledFS: false,
  storeId: undefined,
  zipCode: undefined,
  hideForChooseYourOptions: false,
  directData: null
};

const installServices = params({ storeId: stringType(), zipCode: stringType() }).shape({
  scheduleAMeasure: boolType()
});
if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

const Service = shapeType({
  hasFreeShipping: boolType(),
  freeDeliveryThreshold: numberType({ float: true }),
  locations: arrayOfType(shapeType({
    curbsidePickupFlag: boolType(),
    isBuyInStoreCheckNearBy: boolType(),
    distance: numberType({ float: true }),
    inventory: shapeType({
      isOutOfStock: boolType(),
      isInStock: boolType(),
      isLimitedQuantity: boolType(),
      isUnavailable: boolType(),
      quantity: numberType(),
      maxAllowedBopisQty: numberType(),
      minAllowedBopisQty: numberType()
    }),
    isAnchor: boolType(),
    locationId: stringType(),
    state: stringType(),
    storeName: stringType(),
    storePhone: stringType(),
    type: stringType(),
  })),
  type: stringType(),
  totalCharge: numberType({ float: true }),
});

FulfillmentPodStore.dataModel = {
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  })
    .shape({
      itemId: stringType(),
      info: shapeType({
        globalCustomConfigurator: shapeType({
          customExperience: stringType(),
        }),
        productSubType: shapeType({
          name: stringType()
        }),
        isBrioSku: boolType(),
        isCustomKitchenCabinet: boolType(),
        isGenericProduct: boolType()
      }),
      dataSources: stringType(),
      availabilityType: shapeType({
        buyable: boolType(),
        discontinued: boolType(),
        status: boolType(),
        type: stringType(),
      }),
      fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shapeType({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(Service)
        }))
      })),
      installServices
    })
};

FulfillmentPodStore.displayName = 'FulfillmentPodStore';
